import type { FetchSiteOptionsResponse } from 'services/content/site-options/types'
import {
  SOCIAL,
  LOGIN_BUTTON,
  LOGIN_FORM,
  POLICY_BANNER,
  COMMENTS,
  COMPONENTS,
  GENERAL,
  REGISTER_FORM,
  PATHS,
} from './strings'

const loginButton = (data: FetchSiteOptionsResponse) => {
  const {
    loginButtonText,
    welcomeButtonText,
    viewProfileText,
    logoutText,
  } = data.header.login

  return {
    loginButtonText,
    welcomeButtonText,
    viewProfileText,
    logoutText,
  }
}

const loginForm = (data: FetchSiteOptionsResponse) => {
  const {
    loginTitle,
    loginInstructionText,
    loginEmailInputPlaceholderText,
    loginPasswordInputPlaceholderText,
    loginLinkText,
    createAnAccountText,
    forgotPasswordLinkText,
    errorText,
  } = data.header.login

  return {
    titleText: loginTitle,
    descriptionText: loginInstructionText,
    emailText: loginEmailInputPlaceholderText,
    passwordText: loginPasswordInputPlaceholderText,
    buttonText: loginLinkText,
    errorText,
    createAccountText: createAnAccountText,
    forgotPasswordText: forgotPasswordLinkText,
  }
}

const policyBanner = (data: FetchSiteOptionsResponse) => {
  const { bodyText, acceptButtonText } = data.settings.cookiePolicy
  return {
    bodyText,
    acceptButtonText,
  }
}

const comments = (data: FetchSiteOptionsResponse) => {
  const {
    allCommentsText,
    allCommentsUrlCopiedTooltipText,
    backToLinkText,
    closeCommentsLabelText,
    closeFormLabelText,
    commentButtonText,
    commentedOnText,
    commentOnText,
    commentsLabelText,
    commentUrlCopiedTooltipText,
    descriptionText,
    editButtonText,
    editedText,
    editLabelText,
    filterCommentsLabelText,
    filterCommentsText,
    hideText,
    loginCommentText,
    newestText,
    oldestText,
    postButtonText,
    postPlaceholderText,
    printAllLinkText,
    printLinkText,
    replyButtonText,
    replyMultipleText,
    replyPlaceholderText,
    replySingularText,
    replyToText,
    shareAllLinkText,
    shareLinkText,
    showText,
    sortByText,
    updateButtonText,
    verifyLink,
    wod,
  } = data.comments

  return {
    allCommentsText,
    allCommentsUrlCopiedTooltipText,
    backToLinkText,
    closeCommentsLabelText,
    closeFormLabelText,
    commentButtonText,
    commentedOnText,
    commentOnText,
    commentsLabelText,
    commentUrlCopiedTooltipText,
    descriptionText,
    editedText,
    editButtonText,
    editLabelText,
    filterByLabelText: filterCommentsLabelText,
    filterByText: filterCommentsText,
    formText: {
      edit: {
        buttonText: updateButtonText,
      },
      post: {
        buttonText: postButtonText,
        placeholderText: postPlaceholderText,
      },
      reply: {
        buttonText: replyButtonText,
        placeholderText: replyPlaceholderText,
      },
    },
    hideText,
    loginCommentText,
    newestText,
    oldestText,
    printAllText: printAllLinkText,
    printText: printLinkText,
    replyToText,
    shareAllText: shareAllLinkText,
    shareText: shareLinkText,
    showReplyMultipleText: replyMultipleText,
    showReplySingularText: replySingularText,
    showText,
    sortByText,
    verifyLink,
    wodText: wod,
  }
}

const social = (data: FetchSiteOptionsResponse) => {
  const { mailLabelText, navLabelText, twitterLabelText } = data.social
  return {
    mailLabelText,
    navLabelText,
    twitterLabelText,
  }
}

type TrainingLanguageStrings = Record<
  string,
  { name: string; registerButtonText: string; soldOutButtonText: string }
>

const components = (data: FetchSiteOptionsResponse) => {
  const {
    articleHeader,
    workoutOfTheDay,
    resumeCourseBanner,
    trainingLanguageStrings = [],
  } = data.components

  return {
    articleHeader,
    workoutOfTheDay,
    resumeCourseBanner,
    trainingLanguageStrings: trainingLanguageStrings.reduce(
      (acc, trainingLanguageString) => {
        const {
          language,
          name,
          registerNowText,
          soldOutText,
        } = trainingLanguageString

        language.split(',').forEach((lang) => {
          acc[lang] = {
            name,
            registerButtonText: registerNowText,
            soldOutButtonText: soldOutText,
          }
        })

        return acc
      },
      {} as TrainingLanguageStrings,
    ),
  }
}

const general = (data: FetchSiteOptionsResponse) => {
  const { daysCsv, lessText, moreText, skipToContentButtonText } = data.settings
  const { commentsLabelText: commentLabelText } = data.comments

  return {
    commentLabelText,
    daysOfWeek: daysCsv.split(',').map((day) => day.trim()),
    lessText,
    moreText,
    skipToContentButtonText,
  }
}

const paths = (data: FetchSiteOptionsResponse) => {
  const { paths } = data.settings

  return { ...paths }
}

const registerForm = (data: FetchSiteOptionsResponse) => {
  const {
    ageDisclaimerAccountText,
    backText,
    birthText,
    buttonText,
    dayText,
    emailConfirmText,
    emailText,
    firstNameText,
    fullNameText,
    invalidBirthText,
    invalidEmailConfirmText,
    invalidEmailText,
    invalidNameText,
    invalidPasswordConfirmText,
    invalidPasswordText,
    invalidPrivacyTermsText,
    lastNameText,
    monthOptions,
    monthText,
    passwordConfirmText,
    passwordInstructionText,
    passwordText,
    privacyText,
    successDescriptionText,
    successTitleText,
    termsText,
    titleText,
    yearText,
    yourBirthdayText,
  } = data.header.registerForm

  return {
    titleText,
    firstNameText,
    lastNameText,
    birthText,
    monthText,
    dayText,
    yearText,
    monthOptions: monthOptions.split(',').map((month, index) => ({
      value: index.toString(),
      label: month.trim(),
    })),
    emailText,
    emailConfirmText,
    passwordText,
    passwordConfirmText,
    termsText,
    privacyText,
    buttonText,
    backText,
    invalidNameText,
    invalidBirthText,
    invalidEmailText,
    invalidEmailConfirmText,
    invalidPasswordText,
    invalidPasswordConfirmText,
    invalidPrivacyTermsText,
    successDescriptionText,
    successTitleText,
    fullNameText,
    yourBirthdayText,
    ageDisclaimerAccountText,
    passwordInstructionText,
  }
}

// export function columnModalModuleSchema(entity) {
//   const {
//     closeLabelText,
//     expandAffiliatesLabelText,
//     expandCoursesLabelText,
//     expandDailiesLabelText,
//   } = camelizeKeys(entity)

//   return {
//     [COLUMN_MODAL_MODULE]: {
//       closeLabelText,
//       expandAffiliatesLabelText,
//       expandCoursesLabelText,
//       expandDailiesLabelText,
//     },
//   }
// }

export default (data: FetchSiteOptionsResponse) => {
  return {
    [LOGIN_BUTTON]: loginButton(data),
    [LOGIN_FORM]: loginForm(data),
    [POLICY_BANNER]: policyBanner(data),
    [COMMENTS]: comments(data),
    [SOCIAL]: social(data),
    [COMPONENTS]: components(data),
    [GENERAL]: general(data),
    [PATHS]: paths(data),
    [REGISTER_FORM]: registerForm(data),
  }
}
